import {
  Auth0AccountRole,
  MessageTemplateSummary,
  MessageTemplatesService,
} from "client/openapi";
import { ChannelChecks } from "components/Forms/SendFormControls";
import { Select } from "components/Select";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";

export type TemplateChoiceState = {
  templateId: number | null;
  sms: boolean;
  email: boolean;
};

export const NO_TEMPLATE: TemplateChoiceState = {
  templateId: null,
  sms: true,
  email: false,
};

/** The ways a template can go: the versions it has. */
export function versionsOf(template?: MessageTemplateSummary) {
  return { sms: !!template?.has_sms, email: !!template?.has_email };
}

/**
 * The organization's templates (undefined while loading), loaded once per
 * place. Templates are for admins: anyone else gets none.
 */
export function useTemplates(orgId?: number | null) {
  const [templates, setTemplates] = useState<MessageTemplateSummary[]>();
  useEffect(() => {
    if (!orgId) return;
    MessageTemplatesService.getMessageTemplates({ orgId })
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [orgId]);
  return templates;
}

/**
 * A picker of the organization's message templates. With none, it is
 * shown disabled as "No message templates yet" with a link to make one.
 */
export function TemplatePicker({
  templates,
  templateId,
  onChange,
  placeholder,
  disabled,
}: {
  templates?: MessageTemplateSummary[];
  templateId: number | null;
  onChange: (templateId: number | null) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const none = templates !== undefined && templates.length === 0;
  const options = (templates ?? []).map((template) => ({
    value: String(template.id),
    label: template.name,
  }));
  return (
    <>
      <Select
        options={options}
        value={options.find((o) => Number(o.value) === templateId) || null}
        onChange={(option) => onChange(option ? Number(option.value) : null)}
        isClearable
        placeholder={none ? "No message templates yet" : placeholder}
        isDisabled={disabled || templates === undefined || none}
      />
      {none && (
        <Link
          to="/message-templates"
          className="mt-1 inline-block text-sm font-medium text-gray-700 underline"
        >
          Create a template
        </Link>
      )}
    </>
  );
}

/**
 * Pick a message template to send and how (SMS and/or Email, as the
 * template has them). Used in the invite dialogs, for admins only.
 */
export default function SendTemplateControls({
  orgId,
  value,
  onChange,
  disabled,
}: {
  orgId?: number | null;
  value: TemplateChoiceState;
  onChange: (value: TemplateChoiceState) => void;
  disabled?: boolean;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const admin = currently_selected_role === Auth0AccountRole.ORG_ADMIN;
  const templates = useTemplates(admin ? orgId : null);
  if (!admin) return null;
  const chosen = templates?.find((t) => t.id === value.templateId);
  const versions = versionsOf(chosen);

  return (
    <div className="mt-4">
      <span className="input-label">Send a message template (optional)</span>
      <TemplatePicker
        templates={templates}
        templateId={value.templateId}
        onChange={(templateId) => {
          // A template goes by the ways it has, both when it has both.
          const picked = templates?.find((t) => t.id === templateId);
          onChange(
            picked
              ? { templateId, ...versionsOf(picked) }
              : { ...NO_TEMPLATE, templateId }
          );
        }}
        placeholder="No template selected"
        disabled={disabled}
      />
      {chosen && (
        <div className="mt-2">
          <ChannelChecks
            sms={value.sms}
            email={value.email}
            onChange={(channels) => onChange({ ...value, ...channels })}
            disabled={disabled}
            show={versions}
          />
        </div>
      )}
    </div>
  );
}
